import { IContact } from '@commons/models/contact';
import { IPromise, IQService } from 'angular';

import { Customer, PhoneUnitDeprecatedService } from '@contact/upgrade';

import { UnitOutgoingCall } from './models/unit-outgoing-call';

const MODULE_NAME = 'unitOutgoingCallDeprecatedService';

/**
 * unit outgoing call service available functions
 */
export interface IUnitOutgoingCallDeprecatedService {
  /**
   * contains current unit outgoing call
   */
  unitOutgoingCall: UnitOutgoingCall | undefined;

  /**
   * used to request unit outgoing call form opening
   *
   * @returns {IPromise<any>}
   */
  openUnitOutgoingCallForm(contactAskedCustomerCard?: Customer, phoneNumber?: string): IPromise<any>;

  /**
   * used to request unit outgoing call form opening from a current interaction such as MAIL
   *
   * @returns {IPromise<any>}
   */
  openUnitOutgoingCallFormFromInteraction(responseId: string, contactAskedCustomerCard?: Customer): IPromise<any>;

  /**
   * used to request unit outgoing call form opening from a contact in search view
   *
   * @returns {IPromise<any>}
   */
  openUnitOutgoingCallFormForContact(contactId: string, phone?: string): IPromise<any>;

  /**
   * used to request unit outgoing call form closing
   *
   * @returns {IPromise<any>}
   */
  closeUnitOutgoingCallForm(): IPromise<any>;

  /**
   * used to request dialing of selected target in class member unitOutgoingCall
   *
   * @returns {IPromise<any>}
   */
  dialUnitOutgoingCall(): IPromise<any>;

  /**
   * used to know if unit outgoing call is valid and can be dialed
   *
   * @returns {boolean}
   */
  isUnitOutgoingValid(): boolean;

  /**
   * to know if given contact is a real unit outgoing call
   *
   * @param {IContact} contact
   * @returns {boolean}
   */
  isUnitOutgoingCallContact(contact: IContact): boolean;

  /**
   * to know if given contact is a fake unit outgoing call (used to open form)
   *
   * @param {IContact} contact
   * @returns {boolean}
   */
  isUnitOutgoingCallFakeContact(contact: IContact): boolean;

  /**
   * to know if this agent can open the outcall form
   *
   * @returns {boolean}
   */
  canOpenOutcall(): boolean;

  /**
   * to know if the outcall button must be disabled
   *
   * @returns {boolean}
   */
  outcallDisabled(): boolean;

  /**
   * finish and close all ongoing pause/task
   */
  finishAndClosePauseAndTask(): void;
}

/**
 * unit outgoing call service implementation
 */
export class UnitOutgoingCallDeprecatedService implements IUnitOutgoingCallDeprecatedService {
  static $inject: Array<string> = ['$q', 'phoneUnitDeprecatedService'];

  public name = MODULE_NAME;

  get unitOutgoingCall(): UnitOutgoingCall | undefined {
    return this.phoneUnitDeprecatedService.unitOutgoingCall;
  }

  set unitOutgoingCall(unitOutgoingCall: UnitOutgoingCall | undefined) {
    this.phoneUnitDeprecatedService.unitOutgoingCall = unitOutgoingCall;
  }

  constructor(private readonly $q: IQService, private readonly phoneUnitDeprecatedService: PhoneUnitDeprecatedService) {}

  isUnitOutgoingCallContact(contact: IContact): boolean {
    return PhoneUnitDeprecatedService.isOut(contact);
  }

  static isUnitOutgoingCallContact(contact: IContact): boolean {
    return PhoneUnitDeprecatedService.isOut(contact);
  }

  isUnitOutgoingCallFakeContact(contact: IContact): boolean {
    return PhoneUnitDeprecatedService.isFake(contact);
  }

  static isUnitOutgoingCallFakeContact(contact: IContact): boolean {
    return PhoneUnitDeprecatedService.isFake(contact);
  }

  openUnitOutgoingCallFormFromInteraction(responseId: string, contactAskedCustomerCard?: Customer): IPromise<any> {
    let deferred = this.$q.defer();
    this.phoneUnitDeprecatedService.openForBooking(responseId, contactAskedCustomerCard).then(
      data => deferred.resolve(data),
      err => deferred.reject(err)
    );
    return deferred.promise;
  }

  openUnitOutgoingCallForm(contactAskedCustomerCard?: Customer, phoneNumber?: string): IPromise<any> {
    let deferred = this.$q.defer();
    this.phoneUnitDeprecatedService.openForCustomer(contactAskedCustomerCard, phoneNumber).then(
      data => deferred.resolve(data),
      err => deferred.reject(err)
    );
    return deferred.promise;
  }

  openUnitOutgoingCallFormForContact(contactId: string, phoneNumber?: string): IPromise<any> {
    let deferred = this.$q.defer();
    this.phoneUnitDeprecatedService.openForContact(contactId, phoneNumber).then(
      data => deferred.resolve(data),
      err => deferred.reject(err)
    );
    return deferred.promise;
  }

  closeUnitOutgoingCallForm(): IPromise<any> {
    let deferred = this.$q.defer();
    this.phoneUnitDeprecatedService.close().then(
      data => deferred.resolve(data),
      err => deferred.reject(err)
    );
    return deferred.promise;
  }

  dialUnitOutgoingCall(): IPromise<any> {
    let deferred = this.$q.defer();
    this.phoneUnitDeprecatedService.dial().then(
      data => deferred.resolve(data),
      err => deferred.reject(err)
    );
    return deferred.promise;
  }

  isUnitOutgoingValid(): boolean {
    return this.phoneUnitDeprecatedService.is();
  }

  canOpenOutcall(): boolean {
    return this.phoneUnitDeprecatedService.can();
  }

  outcallDisabled(): boolean {
    return this.phoneUnitDeprecatedService.disabled();
  }

  finishAndClosePauseAndTask(): void {
    this.phoneUnitDeprecatedService.finish();
  }
}

export default MODULE_NAME;
